/**
 * MIÉRT NEM INDUL A KAMERA — és mit mondjunk róla.
 *
 * ── MIÉRT KÜLÖN FÁJL, EGY TISZTA FÜGGVÉNNYEL ────────────────────────────
 *
 * Mert ez DÖNTÉS, nem ragasztóanyag: minden ág más teendőt ad az ügyfélnek,
 * és a rossz ág rosszabb, mint a semmi. Egy „nem sikerült megnyitni a
 * kamerát" üzenet arra a felhasználóra, aki épp letiltotta az engedélyt,
 * pontosan azt hallgatja el, amit tudnia kell.
 *
 * ── A NÉGY ÁG, ÉS MIÉRT PONT EZ A NÉGY ──────────────────────────────────
 *
 * A `getUserMedia` tucatnyi hibanevet ismer, de az ügyfél szempontjából csak
 * az számít, HOGY MIT CSINÁLJON:
 *
 * | Mit tegyen | Mikor |
 * |---|---|
 * | engedélyezze a böngészőben | `NotAllowedError`, `PermissionDeniedError` |
 * | zárja be a másik alkalmazást | `NotReadableError`, `TrackStartError` |
 * | válasszon fájlt helyette | `NotFoundError` — nincs kamera |
 * | szóljon nekünk | minden más |
 *
 * A `SecurityError` szándékosan az elsőbe esik: a böngésző nem biztonságos
 * eredetnél ezt adja, és a felhasználó ott ugyanazt látja — „nem engedi".
 * Hogy miért nem, az a MI hibánk (a PWA HTTPS-en fut), nem az övé.
 *
 * ⚠️ AZ ÜZENET SOHA NEM TARTALMAZZA A NYERS HIBÁT. Egy `NotReadableError:
 * Could not start video source` a telefon képernyőjén ijesztő és
 * használhatatlan; a fejlesztőnek szóló részlet a hibaobjektumban marad.
 */

/** Az i18n-kulcs, amit a felület kiír. A `capture.error.` előtag a hívóé. */
export type CameraErrorKey = 'denied' | 'busy' | 'missing' | 'unknown';

export function cameraErrorKey(error: unknown): CameraErrorKey {
  const name = errorName(error);

  switch (name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
    case 'SecurityError':
      return 'denied';

    case 'NotReadableError':
    case 'TrackStartError':
      return 'busy';

    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return 'missing';

    // Az `OverconstrainedError` SZÁNDÉKOSAN az ismeretlenbe esik. Azt
    // jelenti, hogy a kért beállítás (nálunk a hátlapi kamera) nem
    // teljesíthető — de a felhasználó ezzel nem tud mit kezdeni, mi viszont
    // igen: ez a MI kérésünk hibája, és a naplóba való, nem a képernyőre.
    default:
      return 'unknown';
  }
}

/**
 * A HIBA NEVE, óvatosan.
 *
 * A `getUserMedia` `DOMException`-t dob, aminek van `name`-je — de egy
 * `catch` bármit elkaphat: `undefined`-ot egy elszállt polyfill-ből, stringet
 * egy régi burkolóból. Ha itt `error.name`-et olvasnánk ellenőrzés nélkül, a
 * hibakezelő MAGA dobna, és az ügyfél egy üres képernyőt kapna.
 */
function errorName(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'name' in error) {
    const { name } = error as { name: unknown };
    return typeof name === 'string' ? name : '';
  }
  return '';
}
